import { Language, Category, Product } from "./types";

export const LANGUAGES: Language[] = [
  {
    code: "en",
    name: "English",
    nativeName: "English",
    direction: "ltr",
    flag: "🇺🇸",
  },
  {
    code: "es",
    name: "Spanish",
    nativeName: "Español",
    direction: "ltr",
    flag: "🇪🇸",
  },
  {
    code: "he",
    name: "Hebrew",
    nativeName: "עברית",
    direction: "rtl",
    flag: "🇮🇱",
  },
];

export const ORDER_STATUSES = [
  "pending",
  "preparing",
  "ready",
  "delivered",
  "cancelled",
] as const;

export const USER_ROLES = ["client", "employee", "admin"] as const;

// Sample categories for seeding
export const SAMPLE_CATEGORIES: Omit<Category, "id">[] = [
  {
    name: "Fruits & Vegetables",
    nameEs: "Frutas y Verduras",
    nameHe: "פירות וירקות",
    slug: "fruits-vegetables",
    imageUrl:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
  },
  {
    name: "Dairy & Eggs",
    nameEs: "Lácteos y Huevos",
    nameHe: "חלב וביצים",
    slug: "dairy-eggs",
    imageUrl:
      "https://images.unsplash.com/photo-1563636619-e9143da7973b?w=400&h=300&fit=crop",
  },
  {
    name: "Meat & Seafood",
    nameEs: "Carnes y Mariscos",
    nameHe: "בשר ודגים",
    slug: "meat-seafood",
    imageUrl:
      "https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?w=400&h=300&fit=crop",
  },
  {
    name: "Bakery",
    nameEs: "Panadería",
    nameHe: "מאפייה",
    slug: "bakery",
    imageUrl:
      "https://images.unsplash.com/photo-1509440159596-0249088772ff?w=400&h=300&fit=crop",
  },
  {
    name: "Pantry Staples",
    nameEs: "Productos Básicos",
    nameHe: "מוצרי יסוד",
    slug: "pantry",
    imageUrl:
      "https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=400&h=300&fit=crop",
  },
  {
    name: "Beverages",
    nameEs: "Bebidas",
    nameHe: "משקאות",
    slug: "beverages",
    imageUrl:
      "https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400&h=300&fit=crop",
  },
];

// Sample products for seeding
export const SAMPLE_PRODUCTS: Omit<Product, "id" | "createdAt">[] = [
  // Fruits & Vegetables
  {
    name: "Organic Bananas",
    nameEs: "Plátanos Orgánicos",
    nameHe: "בננות אורגניות",
    description: "Fresh organic bananas, perfect for snacking or smoothies",
    descriptionEs:
      "Plátanos orgánicos frescos, perfectos para aperitivos o batidos",
    descriptionHe: "בננות אורגניות טריות, מושלמות לחטיפים או שייקים",
    price: 2.99,
    stockQuantity: 150,
    categoryIds: ["fruits-vegetables"],
    imageUrl:
      "https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=400&h=400&fit=crop",
    isActive: true,
  },
  {
    name: "Fresh Avocados",
    nameEs: "Aguacates Frescos",
    nameHe: "אבוקדו טרי",
    description: "Ripe and creamy avocados from local farms",
    descriptionEs: "Aguacates maduros y cremosos de granjas locales",
    descriptionHe: "אבוקדו בשל וקרמי מחוות מקומיות",
    price: 1.99,
    stockQuantity: 80,
    categoryIds: ["fruits-vegetables"],
    imageUrl:
      "https://images.unsplash.com/photo-1590005354167-6da97870c757?w=400&h=400&fit=crop",
    isActive: true,
  },
  {
    name: "Red Bell Peppers",
    nameEs: "Pimientos Rojos",
    nameHe: "פלפלים אדומים",
    description: "Sweet and crunchy red bell peppers",
    descriptionEs: "Pimientos rojos dulces y crujientes",
    descriptionHe: "פלפלים אדומים מתוקים ופריכים",
    price: 3.49,
    stockQuantity: 60,
    categoryIds: ["fruits-vegetables"],
    imageUrl:
      "https://images.unsplash.com/photo-1563565375-f3fdfdbefa83?w=400&h=400&fit=crop",
    isActive: true,
  },

  // Dairy & Eggs
  {
    name: "Organic Whole Milk",
    nameEs: "Leche Entera Orgánica",
    nameHe: "חלב מלא אורגני",
    description: "Fresh organic whole milk from grass-fed cows",
    descriptionEs:
      "Leche entera orgánica fresca de vacas alimentadas con pasto",
    descriptionHe: "חלב מלא אורגני טרי מפרות הניזונות מדשא",
    price: 4.99,
    stockQuantity: 40,
    categoryIds: ["dairy-eggs"],
    imageUrl:
      "https://images.unsplash.com/photo-1550583724-b2692b85b150?w=400&h=400&fit=crop",
    isActive: true,
  },
  {
    name: "Farm Fresh Eggs",
    nameEs: "Huevos Frescos de Granja",
    nameHe: "ביצים טריות מהחווה",
    description: "Free-range eggs from happy hens",
    descriptionEs: "Huevos de gallinas de corral felices",
    descriptionHe: "ביצים מתרנגולות חופשיות ומאושרות",
    price: 5.99,
    stockQuantity: 100,
    categoryIds: ["dairy-eggs"],
    imageUrl:
      "https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=400&h=400&fit=crop",
    isActive: true,
  },

  // Meat & Seafood
  {
    name: "Grass-Fed Ground Beef",
    nameEs: "Carne Molida de Res Alimentada con Pasto",
    nameHe: "בשר טחון מפרות הניזונות מדשא",
    description: "Premium grass-fed ground beef, 80/20 lean",
    descriptionEs:
      "Carne molida premium de res alimentada con pasto, 80/20 magra",
    descriptionHe: "בשר טחון מובחר מפרות הניזונות מדשא, 80/20 רזה",
    price: 8.99,
    stockQuantity: 25,
    categoryIds: ["meat-seafood"],
    imageUrl:
      "https://images.unsplash.com/photo-1588347818104-6b4b4d7a5d4a?w=400&h=400&fit=crop",
    isActive: true,
  },

  // Bakery
  {
    name: "Artisan Sourdough Bread",
    nameEs: "Pan Artesanal de Masa Madre",
    nameHe: "לחם שאור אמן",
    description: "Handcrafted sourdough bread with a perfect crust",
    descriptionEs: "Pan de masa madre hecho a mano con una corteza perfecta",
    descriptionHe: "לחם שאור בעבודת ידיים עם קרום מושלם",
    price: 6.99,
    stockQuantity: 20,
    categoryIds: ["bakery"],
    imageUrl:
      "https://images.unsplash.com/photo-1549931319-a545dcf3bc73?w=400&h=400&fit=crop",
    isActive: true,
  },

  // Pantry Staples
  {
    name: "Organic Brown Rice",
    nameEs: "Arroz Integral Orgánico",
    nameHe: "אורז מלא אורגני",
    description: "Long-grain organic brown rice, perfect for healthy meals",
    descriptionEs:
      "Arroz integral orgánico de grano largo, perfecto para comidas saludables",
    descriptionHe: "אורז מלא אורגני גרגיר ארוך, מושלם לארוחות בריאות",
    price: 4.49,
    stockQuantity: 75,
    categoryIds: ["pantry"],
    imageUrl:
      "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=400&fit=crop",
    isActive: true,
  },

  // Beverages
  {
    name: "Cold-Pressed Orange Juice",
    nameEs: "Jugo de Naranja Prensado en Frío",
    nameHe: "מיץ תפוזים סחוט בקור",
    description: "Fresh cold-pressed orange juice with no added sugar",
    descriptionEs: "Jugo de naranja fresco prensado en frío sin azúcar añadida",
    descriptionHe: "מיץ תפוזים טרי סחוט בקור ללא תוספת סוכר",
    price: 7.99,
    stockQuantity: 30,
    categoryIds: ["beverages"],
    imageUrl:
      "https://images.unsplash.com/photo-1621506289937-a8e4df240d0b?w=400&h=400&fit=crop",
    isActive: true,
  },
];

export const CURRENCY = {
  MXN: "MXN",
  symbol: "MX$",
  decimals: 2,
};

export const DEFAULT_LANGUAGE = "es"; // Spanish for Mexico
export const SUPPORTED_LANGUAGES = ["en", "es", "he"] as const;
